/**
 * My-profile hooks — Phase 3.6.B.
 *
 * Pairs with BE routes:
 *   GET   /api/me/profile           → MyProfile
 *   PATCH /api/me/profile           → MyProfile (update name / title / phone / timezone)
 *   POST  /api/me/avatar            → uses the FU-D presigned upload pattern
 *                                     with `parentKind='profile'`
 *
 * Email is NOT updatable here — Cognito owns it. The profile form
 * renders the email read-only and disables the input.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiClient } from '@/api/client'
import { ME_QUERY_KEY } from '@/features/me/use-me'

import type { MyProfile, MyProfileInput } from './types'

export const MY_PROFILE_QUERY_KEY = ['my-profile'] as const

export function useMyProfile() {
  return useQuery({
    queryKey: MY_PROFILE_QUERY_KEY,
    queryFn: async () => {
      try {
        const res = await apiClient<{ data: MyProfile }>('/api/me/profile')
        return res.data
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return null
        }
        throw err
      }
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useUpdateMyProfile() {
  const queryClient = useQueryClient()
  return useMutation<MyProfile, Error, MyProfileInput>({
    mutationFn: async (input) => {
      const res = await apiClient<{ data: MyProfile }>('/api/me/profile', {
        method: 'PATCH',
        body: input,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY })
      // The `useMe` cache also carries name + avatar — refresh it so the
      // sidebar / appshell update immediately.
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}

/**
 * Attach an uploaded avatar to the current user.
 *
 * Avatars land in the public-assets CloudFront bucket
 * (`avatars-{stage}.3mote.com`). The upload flow returns the absolute
 * URL directly; this hook writes it verbatim into `users.avatar_url`
 * via `PATCH /api/me/profile`. The BE cascades a delete of the old
 * file when the URL changes (see
 * `3mote-be-new/src/me/router.ts` cascade block).
 */
export function useSetMyAvatar() {
  const queryClient = useQueryClient()
  return useMutation<MyProfile, Error, { avatarUrl: string }>({
    mutationFn: async ({ avatarUrl }) => {
      const res = await apiClient<{ data: MyProfile }>('/api/me/profile', {
        method: 'PATCH',
        body: { avatarUrl },
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_PROFILE_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })
    },
  })
}
